import { intersection, isEmpty } from 'lodash/fp';
import { DataKey, DataType } from 'types';

import { getDataTypeConfig } from './data-type-config';

export const CauseOutcome = 'cause_outcome';
export const SequelaOutcome = 'sequela_outcome';
export const RiskAttribution = 'risk_attribution';
export const RiskPrevalence = 'risk_prevalence';
export const RiskExposureRecords = 'risk_exposure_records';
export const EtiologyRecords = 'etiology_records';
export const ImpairmentRecords = 'impairment_records';
export const InjuryRecords = 'injury_records';
export const SevRecords = 'sev_records';
export const CovariateRecords = 'covariate_records';
export const SdohRecords = 'sdoh_records';
export const PopulationRecords = 'population_records';
export const TakedaDengueRecords = 'takeda_dengue_records';
export const Covid19Records = 'covid19_records';
export const CauseCoprevalenceRecords = 'cause_coprevalence_records';
export const ProjectedComorbidityCausesOutcomeRecords =
    'projected_comorbidity_causes_outcome_records';
export const CauseAssociatedCoprevalenceRecords = 'cause_associated_coprevalence_records';
export const CauseAssociatedComorbidityStudyCoefficients =
    'cause_associated_comorbidity_study_coefficients';
export const EDPOnetimeIndicators = 'early_detection_onetime_indicators';
export const EDPTimelineIndicators = 'early_detection_timeline_indicators';
export const LifeExpectancyRecords = 'life_expectancy_records';
export const AntimicrobialResistanceRecords = 'antimicrobial_resistance_records';
export const MultipleMyelomaOutputShell = 'multiple_myeloma_output_shell';
export const MultipleMyelomaSurvivalResults = 'multiple_myeloma_survival_results';
export const MultipleMyelomaPopulation = 'multiple_myeloma_population_records';
export const HealthThreatsMetricsRecords = 'health_threat_metrics_records';
export const RaceEthnicityEyeConditionsRecords = 'race_and_ethnicity_eye_conditions_records';
export const ExpenditureRecords = 'expenditure_records';
export const AllDataTypes = 'all';

export const getDataTypesWithoutCondition = (): DataType[] => [
    Covid19Records,
    LifeExpectancyRecords,
    PopulationRecords,
    MultipleMyelomaPopulation,
];

export const REI_DATA_TYPES = [
    RiskAttribution,
    RiskPrevalence,
    EtiologyRecords,
    ImpairmentRecords,
    InjuryRecords,
    SevRecords,
];

export const COMORBIDITY_DATA_TYPES = [
    CauseCoprevalenceRecords,
    ProjectedComorbidityCausesOutcomeRecords,
    CauseAssociatedCoprevalenceRecords,
    CauseAssociatedComorbidityStudyCoefficients,
];

export const COMPATIBLE_DATA_TYPE_GROUPS = [
    [CauseOutcome, SequelaOutcome],
    [EDPTimelineIndicators, EDPOnetimeIndicators],
];

export const dataTypeHasPrimaryEntity = (dataType: DataType): boolean =>
    !getDataTypesWithoutCondition().includes(dataType) && dataType !== TakedaDengueRecords;

export const getDataTypePrimaryEntityKey = (dataType: DataType): DataKey =>
    getDataTypeConfig()[dataType].filterKey;

export const getDataTypeLocalePrefix = (dataType: DataType): DataKey =>
    getDataTypeConfig()[dataType]?.localePrefix;

export const getDataTypeEntityHierarchyKey = (dataType: DataType): DataKey =>
    getDataTypeConfig()[dataType].entityHierarchyKey;

export const areComorbidityDataTypes = (dataTypes: DataType[]): boolean =>
    !isEmpty(intersection(dataTypes, COMORBIDITY_DATA_TYPES));

export const areReiDataTypes = (dataTypes: DataType[]): boolean =>
    !isEmpty(intersection(dataTypes, REI_DATA_TYPES));

export const isComorbidityCoprevalenceDataType = (dataType: DataType): boolean =>
    [CauseCoprevalenceRecords, CauseAssociatedCoprevalenceRecords].includes(dataType);

export const isComorbidityCooccurrenceDataType = (dataType: DataType): boolean =>
    [CauseCoprevalenceRecords, ProjectedComorbidityCausesOutcomeRecords].includes(dataType);

export const areComorbidityCoprevalenceDataTypes = (dataTypes: DataType[]): boolean =>
    !isEmpty(
        intersection(dataTypes, [CauseCoprevalenceRecords, CauseAssociatedCoprevalenceRecords])
    );

export const areComorbidityStudyDataTypes = (dataTypes: DataType[]): boolean =>
    !isEmpty(
        intersection(dataTypes, [
            ProjectedComorbidityCausesOutcomeRecords,
            CauseAssociatedComorbidityStudyCoefficients,
        ])
    );
